/**
 * AdminArticlePreview — shows the article + its generated infographic in
 * the same layout the live `/learn/{slug}` page will render once
 * published. Lets the admin sanity-check the visual + copy before
 * pressing Publish.
 *
 * Route: /admin/content/:packageId/article-preview
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Image as ImageIcon, AlertCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { adminArticlePreview } from "@/lib/api";

// Markdown component map — defined once at module scope so React doesn't
// re-mount the markdown tree on every render of `ArticleMarkdown`.
const MD_P = ({ node, ...props }) => <p className="text-base text-bl leading-relaxed" {...props} />;
const MD_STRONG = ({ node, ...props }) => <strong className="font-semibold text-bl" {...props} />;
const MD_EM = ({ node, ...props }) => <em className="italic" {...props} />;
const MD_A = ({ node, ...props }) => (
    <a
        className="text-bl-primary underline hover:text-bl-primary/80"
        target="_blank"
        rel="noreferrer"
        {...props}
    />
);
const MD_UL = ({ node, ...props }) => (
    <ul className="list-disc pl-5 space-y-1.5 text-base text-bl leading-relaxed" {...props} />
);
const MD_OL = ({ node, ...props }) => (
    <ol className="list-decimal pl-5 space-y-1.5 text-base text-bl leading-relaxed" {...props} />
);
const MD_LI = ({ node, ...props }) => <li className="text-base text-bl leading-relaxed" {...props} />;
const MD_BLOCKQUOTE = ({ node, ...props }) => (
    <blockquote className="border-l-4 border-bl-primary/60 pl-4 italic text-bl-muted my-3" {...props} />
);
const MD_H1 = ({ node, ...props }) => <h1 className="font-display font-semibold text-3xl text-bl mt-6 mb-3" {...props} />;
const MD_H2 = ({ node, ...props }) => <h2 className="font-display font-semibold text-2xl text-bl mt-6 mb-3" {...props} />;
const MD_H3 = ({ node, ...props }) => <h3 className="font-display font-semibold text-lg text-bl mt-4 mb-2" {...props} />;
const MD_CODE = ({ inline, node, ...props }) => (
    inline ? (
        <code className="px-1 py-0.5 rounded bg-bl-bg text-[0.92em] font-mono" {...props} />
    ) : (
        <code className="block p-3 rounded bg-bl-bg text-sm font-mono whitespace-pre-wrap" {...props} />
    )
);
const MD_TABLE = ({ node, ...props }) => (
    <div className="overflow-x-auto my-3">
        <table className="w-full border-collapse text-sm" {...props} />
    </div>
);
const MD_TH = ({ node, ...props }) => (
    <th className="border border-bl-line bg-bl-bg px-2 py-1 text-left font-semibold text-bl" {...props} />
);
const MD_TD = ({ node, ...props }) => (
    <td className="border border-bl-line px-2 py-1 text-bl" {...props} />
);

const MARKDOWN_COMPONENTS = {
    p: MD_P,
    strong: MD_STRONG,
    em: MD_EM,
    a: MD_A,
    ul: MD_UL,
    ol: MD_OL,
    li: MD_LI,
    blockquote: MD_BLOCKQUOTE,
    h1: MD_H1,
    h2: MD_H2,
    h3: MD_H3,
    code: MD_CODE,
    table: MD_TABLE,
    th: MD_TH,
    td: MD_TD,
};
const MARKDOWN_PLUGINS = [remarkGfm];

/**
 * Render a body_markdown block with full markdown support — paragraphs,
 * **bold**, *italics*, [links](url), lists, blockquotes, headings, and
 * GFM tables.
 */
function ArticleMarkdown({ children }) {
    if (!children || typeof children !== "string") return null;
    return (
        <ReactMarkdown
            remarkPlugins={MARKDOWN_PLUGINS}
            components={MARKDOWN_COMPONENTS}
        >
            {children}
        </ReactMarkdown>
    );
}

export default function AdminArticlePreview() {
    const { packageId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminArticlePreview(packageId);
                if (!alive) return;
                setData(r);
                setError(null);
            } catch (e) {
                if (alive) setError(e?.response?.data?.detail || e.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [packageId]);

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-16 text-center text-bl-muted">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
                Loading preview…
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-16">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="bl-btn-ghost text-xs gap-1 mb-4"
                    data-testid="article-preview-back-btn"
                >
                    <ArrowLeft className="h-3 w-3" /> Back
                </button>
                <div className="bl-card p-6 border-l-4 border-l-[var(--bl-rust)]">
                    <h2 className="font-display font-semibold text-bl flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-[var(--bl-rust)]" />
                        Preview unavailable
                    </h2>
                    <p className="text-sm text-bl-muted mt-2">{error}</p>
                </div>
            </div>
        );
    }

    const {
        title,
        slug,
        hero_intro,
        key_takeaways = [],
        sections = [],
        faq = [],
        cta_blocks = [],
        infographic_image_url,
        infographic_alt,
        estimated_read_minutes,
        published,
        package_status,
    } = data;

    return (
        <div className="min-h-screen bg-bl-bg" data-testid="admin-article-preview">
            {/* Admin chrome */}
            <div className="border-b border-bl/30 bg-bl-surface">
                <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(`/admin/content?package=${packageId}`)}
                            className="bl-btn-ghost text-xs gap-1"
                            data-testid="article-preview-back-btn"
                        >
                            <ArrowLeft className="h-3 w-3" /> Back to editor
                        </button>
                        <div className="text-xs text-bl-muted">
                            <span className="font-mono">{packageId}</span>
                            {slug && <span> · /learn/{slug}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em]">
                        <span className={
                            "px-2 py-1 rounded-full font-medium " +
                            (published
                                ? "bg-[var(--bl-green-soft)] text-[var(--bl-green)]"
                                : "bg-[var(--bl-amber-soft)] text-[var(--bl-amber)]")
                        }
                        data-testid="article-preview-status-pill"
                        >
                            {published ? "Published" : `Draft · ${package_status || "generated"}`}
                        </span>
                    </div>
                </div>
            </div>

            {/* Preview body */}
            <article className="max-w-3xl mx-auto px-6 py-10 space-y-6">
                <header className="space-y-3">
                    {estimated_read_minutes && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                            {estimated_read_minutes} min read
                        </div>
                    )}
                    <h1
                        className="font-display font-bold text-3xl sm:text-4xl text-bl leading-tight tracking-tight"
                        data-testid="article-preview-title"
                    >
                        {title || "Untitled article"}
                    </h1>
                    {hero_intro && (
                        <p className="text-base sm:text-lg text-bl-muted leading-relaxed">
                            {hero_intro}
                        </p>
                    )}
                </header>

                {/* Infographic */}
                {infographic_image_url ? (
                    <figure
                        className="rounded-xl overflow-hidden border border-bl/30 bg-bl-surface"
                        data-testid="article-preview-infographic"
                    >
                        <img
                            src={
                                infographic_image_url.startsWith("http")
                                    ? infographic_image_url
                                    : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${infographic_image_url}`
                            }
                            alt={infographic_alt || title}
                            className="w-full h-auto block"
                        />
                        <figcaption className="px-4 py-2 text-[11px] text-bl-muted border-t border-bl/30">
                            Article infographic · pulled into the weekly email
                            package on send.
                        </figcaption>
                    </figure>
                ) : (
                    <div
                        className="rounded-xl border-2 border-dashed border-bl/30 p-6 text-center bg-bl-surface"
                        data-testid="article-preview-missing-infographic"
                    >
                        <ImageIcon className="h-8 w-8 mx-auto text-bl-muted/60 mb-2" />
                        <p className="text-sm font-medium text-bl mb-1">
                            Missing infographic
                        </p>
                        <p className="text-xs text-bl-muted">
                            The infographic image hasn&rsquo;t been generated yet. Open the
                            Images tab in the editor to generate it before publishing.
                        </p>
                    </div>
                )}

                {/* Key takeaways */}
                {key_takeaways.length > 0 && (
                    <section
                        className="bl-card p-5 space-y-3"
                        data-testid="article-preview-takeaways"
                    >
                        <h2 className="text-xs uppercase tracking-[0.18em] text-bl-primary font-semibold">
                            Key takeaways
                        </h2>
                        <ul className="space-y-2 text-sm text-bl">
                            {key_takeaways.map((t, i) => (
                                <li key={i} className="flex gap-2">
                                    <span className="text-bl-primary font-mono">·</span>
                                    <span>{typeof t === "string" ? t : t.text || JSON.stringify(t)}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Sections — supports the three article shapes the
                    generators emit: `paragraphs[]`, `body_markdown`
                    (multi-paragraph + lists + headings via react-markdown),
                    and nested `subsections[]` (heading + body_markdown). */}
                {sections.map((sec, i) => (
                    <section
                        key={i}
                        className="space-y-4"
                        data-testid={`article-preview-section-${i}`}
                    >
                        {sec.heading && (
                            <h2 className="font-display font-semibold text-2xl text-bl tracking-tight">
                                {sec.heading}
                            </h2>
                        )}
                        {/* Shape A: explicit paragraphs[] */}
                        {Array.isArray(sec.paragraphs)
                            && sec.paragraphs.map((p, j) => (
                                <p key={j} className="text-base text-bl leading-relaxed">{p}</p>
                            ))}
                        {/* Shape B: body_markdown — full markdown including
                            paragraphs, bold, italics, lists, blockquotes,
                            headings, and GFM tables. */}
                        {sec.body_markdown && (
                            <div className="space-y-4" data-testid={`article-preview-section-${i}-body`}>
                                <ArticleMarkdown>{sec.body_markdown}</ArticleMarkdown>
                            </div>
                        )}
                        {/* Shape C: legacy single `body` string */}
                        {sec.body && !sec.body_markdown && !sec.paragraphs && (
                            <p className="text-base text-bl leading-relaxed whitespace-pre-line">
                                {sec.body}
                            </p>
                        )}
                        {/* Nested subsections */}
                        {Array.isArray(sec.subsections) && sec.subsections.map((sub, k) => (
                            <div key={k} className="space-y-3 pl-4 border-l-2 border-bl-primary/40">
                                {sub.heading && (
                                    <h3 className="font-display font-semibold text-lg text-bl">
                                        {sub.heading}
                                    </h3>
                                )}
                                {sub.body_markdown && (
                                    <ArticleMarkdown>{sub.body_markdown}</ArticleMarkdown>
                                )}
                                {Array.isArray(sub.bullets) && sub.bullets.length > 0 && (
                                    <ul className="list-disc pl-5 space-y-1.5">
                                        {sub.bullets.map((b, m) => (
                                            <li key={m} className="text-base text-bl leading-relaxed">
                                                {typeof b === "string" ? b : JSON.stringify(b)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </section>
                ))}

                {/* CTA blocks (rendered minimal) */}
                {cta_blocks.length > 0 && (
                    <section className="space-y-3" data-testid="article-preview-ctas">
                        <h2 className="text-xs uppercase tracking-[0.18em] text-bl-primary font-semibold">
                            CTAs
                        </h2>
                        {cta_blocks.map((c, i) => (
                            <div key={i} className="bl-card p-4 border-l-4 border-l-bl-primary">
                                <div className="text-sm font-medium text-bl">{c.headline || c.title}</div>
                                {c.body && <p className="text-xs text-bl-muted mt-1">{c.body}</p>}
                                {c.button_label && (
                                    <span className="inline-block mt-2 px-3 py-1.5 rounded bg-bl-primary text-bl-surface text-xs">
                                        {c.button_label}
                                    </span>
                                )}
                            </div>
                        ))}
                    </section>
                )}

                {/* FAQ */}
                {faq.length > 0 && (
                    <section className="space-y-3" data-testid="article-preview-faq">
                        <h2 className="font-display font-semibold text-xl text-bl">FAQ</h2>
                        {faq.map((q, i) => (
                            <details key={i} className="bl-card p-4">
                                <summary className="font-medium text-sm text-bl cursor-pointer">
                                    {q.question || q.q}
                                </summary>
                                <p className="text-sm text-bl-muted mt-2">{q.answer || q.a}</p>
                            </details>
                        ))}
                    </section>
                )}
            </article>
        </div>
    );
}
